// Real company data for Advanced Engineered Environments website
// Based on official company presentation

export const companyInfo = {
  name: "Advanced Engineered Environments",
  tagline: "Innovative Spaces to Test, Manufacture, Store & Grow",
  description: "Dedicated to turn-key solutions in exceptional design, manufacture, construction, commissioning, validation, and long-term support of specialized environmental rooms, chambers, and cleanrooms. Helping you create specialty working environments since 1989.",
  phone: "+1 (647) 449-1363",
  email: "info@aee.ca",
  address: "1900 Clark Blvd Unit 6, Brampton, ON, L6T0E9",
  founded: "1989",
  experience: "30+",
  logo: "https://customer-assets.emergentagent.com/job_0caad674-da07-42ae-8497-83c3015cb5cd/artifacts/d09l0qqk_AEE_logo_image.svg",
  missionStatement: "For over 30 years, AEE has set the standard in GMP cleanroom solutions — delivering precision-engineered controlled environments for biopharma and healthcare, where compliance meets innovation."
};

export const heroData = {
  headline: "Innovative Spaces to Test, Manufacture, Store & Grow",
  subheadline: "Industry leaders in controlled environments and cleanroom technology. Over 30 years of expertise delivering turn-key solutions from design through certification.",
  ctaPrimary: "Request a Consultation",
  ctaSecondary: "See Our Process",
  stats: [
    { value: "30+", label: "Years Experience" },
    { value: "500+", label: "Projects Completed" },
    { value: "60k+", label: "Sq Ft Largest Project" },
    { value: "ISO 5-8", label: "Classifications" }
  ]
};

export const valuePropositions = [
  {
    id: 1,
    title: "Local Canadian Presence",
    description: "Direct access to our team with local expertise and support throughout your project.",
    icon: "MapPin"
  },
  {
    id: 2,
    title: "Large Project Experience",
    description: "Proven track record on major cleanroom projects up to 60,000+ square feet.",
    icon: "Building2"
  },
  {
    id: 3,
    title: "High Quality Products",
    description: "UL Listed materials with premium finishes designed for low bioburden.",
    icon: "Award"
  },
  {
    id: 4,
    title: "Excellent Lead Times",
    description: "Significantly reduced architectural component lead times through efficient processes.",
    icon: "Clock"
  },
  {
    id: 5,
    title: "Technical Design Support",
    description: "Expert guidance during design with BIM, 3D modeling, and engineering expertise.",
    icon: "Wrench"
  }
];

export const processSteps = [
  {
    step: 1,
    title: "Comprehensive Needs Assessment and Front-End Engineering Planning",
    duration: "Typically 1-2 Weeks",
    description: "We assess your requirements, space, and regulatory needs. Comprehensive discovery to define your project scope.",
    deliverables: ["Site Assessment Report", "Requirements Document", "Preliminary Design", "Budget Estimate"]
  },
  {
    step: 2,
    title: "Design & Engineering",
    duration: "Typically 3-6 Weeks",
    description: "Using ALLPLAN BIM, SOLIDWORKS, AutoCAD, and Revit, our engineers create complete 2D layouts and 3D designs including BMS/BAS P&ID and electrical single lines.",
    deliverables: ["2D Layouts", "3D Architectural Panels & Ducting", "BMS/BAS P&ID", "Electrical Single Line", "3D Ductwork Routing"]
  },
  {
    step: 3,
    title: "Procurement & Fabrication",
    duration: "Typically 4-8 Weeks",
    description: "High quality modular components manufactured with UL listed materials. Pre-assembly in controlled conditions.",
    deliverables: ["Modular Wall Systems", "BMS/BAS Components", "Electrical Systems", "Quality Verified Materials"]
  },
  {
    step: 4,
    title: "Construction & Installation",
    duration: "Typically 3-6 Weeks",
    description: "Fast installation of all architectural, mechanical, and electrical components by our trained crews with integrated BMS/BAS automation.",
    deliverables: ["Complete Installation", "Systems Integration", "Door Interlock Governance", "Controls Setup"]
  },
  {
    step: 5,
    title: "Commissioning & Certification",
    duration: "Typically 1-2 Weeks",
    description: "Start-up, testing, and certification of all architectural, mechanical, and electrical components with comprehensive documentation.",
    deliverables: ["Performance Testing", "Certification Documentation", "O&M Manuals", "Staff Training"]
  }
];

export const services = [
  {
    id: 1,
    title: "Complete Turnkey Projects",
    description: "Full design-build capabilities covering all architectural, mechanical, electrical components with BMS/BAS integration.",
    icon: "Package",
    features: ["Single-source coordination", "Design through certification", "Integrated delivery"]
  },
  {
    id: 2,
    title: "Cleanrooms",
    description: "ISO 5-8 classified cleanrooms for pharmaceutical, biotech, and medical device manufacturing with premium modular construction.",
    icon: "Box",
    features: ["ISO 5-8 classifications", "cGMP compliant", "Modular construction"]
  },
  {
    id: 3,
    title: "Laboratories",
    description: "Specialized laboratory environments designed for research, development, and testing applications.",
    icon: "FlaskConical",
    features: ["Research labs", "Testing facilities", "Development spaces"]
  },
  {
    id: 4,
    title: "Environmental Controlled Rooms",
    description: "Temperature and humidity controlled environments including incubators, freezers, and clean cold rooms.",
    icon: "Thermometer",
    features: ["Temperature control", "Humidity control", "Clean cold rooms"]
  },
  {
    id: 5,
    title: "Design-Assist Services",
    description: "Design-assist the prime AE on cleanroom and environmental room specifications with our deep technical expertise.",
    icon: "PenTool",
    features: ["Technical consultation", "Specification support", "BIM coordination"]
  },
  {
    id: 6,
    title: "Training Programs",
    description: "Comprehensive training including GMP pharma compliance, mechanical equipment operations, procedural requirements, and general equipment maintenance.",
    icon: "GraduationCap",
    features: ["GMP training", "Equipment handling", "Cleaning and maintenance"]
  }
];

export const industries = [
  {
    id: 1,
    name: "Pharmaceutical & Biotech",
    description: "Cleanroom solutions for drug manufacturing, API production, and biopharmaceutical research with GMP compliance.",
    icon: "Pill",
    image: "https://images.unsplash.com/photo-1748350985297-bfca7994fc1a"
  },
  {
    id: 2,
    name: "Gene & Cell Therapy",
    description: "Advanced multi-suite facilities for cutting-edge gene cell therapy manufacturing and research.",
    icon: "Dna",
    image: "https://images.pexels.com/photos/9574329/pexels-photo-9574329.jpeg"
  },
  {
    id: 3,
    name: "Medical Device Manufacturing",
    description: "ISO 7/8 environments for medical device assembly including COVID test kit production facilities.",
    icon: "Heart",
    image: "https://images.pexels.com/photos/6129585/pexels-photo-6129585.jpeg"
  },
  {
    id: 4,
    name: "Healthcare & Hospitals",
    description: "Cleanroom facilities for hospitals, research institutions, and healthcare organizations.",
    icon: "Hospital",
    image: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d"
  },
  {
    id: 5,
    name: "Active Pharmaceutical Ingredients",
    description: "Controlled API manufacturing environments with Health Canada approved specifications.",
    icon: "FlaskConical",
    image: "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69"
  },
  {
    id: 6,
    name: "Institutional & Universities",
    description: "Research cleanrooms for universities and institutional settings across North America.",
    icon: "GraduationCap",
    image: "https://images.unsplash.com/photo-1562774053-701939374585"
  }
];

export const differentiators = [
  {
    id: 1,
    title: "30+ Years Industry Experience",
    description: "Since 1989, we've been helping organizations create specialty working environments. Our deep expertise spans pharmaceuticals, biotech, medical devices, and institutional settings.",
    icon: "Award"
  },
  {
    id: 2,
    title: "BIM-Integrated Design Process",
    description: "We utilize ALLPLAN BIM, SOLIDWORKS, AutoCAD, Pro/ENGINEER, and Revit to deliver precise 3D layouts of architectural panels, ducting, utilities, and piping.",
    icon: "Layers"
  },
  {
    id: 3,
    title: "Premium Modular Construction",
    description: "High quality modular components manufactured with UL listed materials. Pre-assembly in controlled conditions.",
    icon: "Box"
  },
  {
    id: 4,
    title: "Comprehensive Quality System",
    description: "ISO 9001:2015 certified with cGMP compliance, supplier qualification & auditing, CAPA programs, and key performance metrics tracking.",
    icon: "Shield"
  }
];

export const certifications = [
  { name: "ISO 9001:2015", description: "Quality Management System" },
  { name: "cGMP", description: "Consistent Quality" },
  { name: "UL Listed", description: "Safety Certified" },
  { name: "ULC", description: "Canadian Safety Standards" },
  { name: "ISPE", description: "Pharmaceutical Engineering" }
];

export const cleanroomClasses = [
  { class: "ISO 5", particles: "100,000", typical: "Pharmaceutical Sterile Filling" },
  { class: "ISO 6", particles: "1,000,000", typical: "Gene Cell Therapy" },
  { class: "ISO 7", particles: "10,000,000", typical: "Medical Device Assembly" },
  { class: "ISO 8", particles: "100,000,000", typical: "Controlled Manufacturing" }
];

export const testimonials = [
  {
    id: 1,
    name: "Operations Director",
    role: "VP of Operations",
    company: "Major Pharmaceutical Company",
    content: "AEE delivered our 25,000 sq ft ISO 5-8 sterile filling suite on schedule. Their turnkey approach simplified coordination and their technical expertise was evident throughout the project.",
    rating: 5,
    highlight: "On-schedule delivery"
  },
  {
    id: 2,
    name: "Facilities Manager",
    role: "Facilities Director",
    company: "Gene Therapy Research Center",
    content: "Our advanced 2,500 sq ft multi-suite Gene Cell Therapy facility required complex specifications. AEE's team understood our unique requirements and delivered a facility that met all our needs.",
    rating: 5,
    highlight: "Complex requirements met"
  },
  {
    id: 3,
    name: "Project Lead",
    role: "Construction Manager",
    company: "Medical Device Manufacturer",
    content: "The 4,600 sq ft ISO 7/8 medical device suite for COVID test kit manufacturing was completed efficiently. AEE's modular construction approach minimized site disruption.",
    rating: 5,
    highlight: "Minimal disruption"
  }
];

export const projects = [
  {
    id: 1,
    title: "ISO 5-8 MAL/PAL Sterile Filling Suite",
    location: "Canada",
    classification: "ISO 5-8",
    size: "25,000 sq ft",
    timeline: "Completed",
    scope: "Design-build sterile liquids filling suite",
    image: "https://images.unsplash.com/photo-1748350985297-bfca7994fc1a"
  },
  {
    id: 2,
    title: "Gene Cell Therapy Multi-Suite Facility",
    location: "Canada",
    classification: "ISO 5/6/7/8",
    size: "2,500 sq ft",
    timeline: "Completed",
    scope: "Advanced multi-suite Gene Cell Therapy facility",
    image: "https://images.pexels.com/photos/9574329/pexels-photo-9574329.jpeg"
  },
  {
    id: 3,
    title: "Medical Device Manufacturing Suite",
    location: "Canada",
    classification: "ISO 7/8",
    size: "4,600 sq ft",
    timeline: "Completed",
    scope: "COVID Test Kits manufacturing suite",
    image: "https://images.pexels.com/photos/6129585/pexels-photo-6129585.jpeg"
  },
  {
    id: 4,
    title: "Health Canada Approved Manufacturing",
    location: "Canada",
    classification: "ISO 7/8",
    size: "5,600 sq ft",
    timeline: "Completed",
    scope: "10-suite controlled substances facility",
    image: "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69"
  },
  {
    id: 5,
    title: "Pharma Manufacturing Facility & Lab",
    location: "Canada",
    classification: "ISO 5/6/7",
    size: "12,000 sq ft",
    timeline: "Completed",
    scope: "Complete design-build pharmaceutical facility",
    image: "https://images.unsplash.com/photo-1748000970909-845f4aa144d2"
  },
  {
    id: 6,
    title: "Dual Campus Cleanroom Expansion",
    location: "Canada",
    classification: "ISO 5-8",
    size: "60,000+ sq ft",
    timeline: "Completed",
    scope: "Two new campus cleanroom facilities",
    image: "https://images.unsplash.com/photo-1584677123777-742161156eb7"
  }
];

export const clients = [
  { name: "Pfizer", logo: "https://commons.wikimedia.org/wiki/Special:FilePath/Pfizer_(2021).svg" },
  { name: "Novartis", logo: "https://commons.wikimedia.org/wiki/Special:FilePath/Novartis-Logo.svg" },
  { name: "Sanofi", logo: "https://commons.wikimedia.org/wiki/Special:FilePath/Sanofi-2022.svg" },
  { name: "GSK", logo: "https://commons.wikimedia.org/wiki/Special:FilePath/GSK_Logo_PNG5.png" },
  { name: "Apotex", logo: null },
  { name: "Taro", logo: null },
  { name: "Catalent", logo: null },
  { name: "Elanco", logo: null },
  { name: "University of Toronto", logo: "https://commons.wikimedia.org/wiki/Special:FilePath/UofT_Wordmark.png" },
  { name: "UBC", logo: "https://commons.wikimedia.org/wiki/Special:FilePath/Ubc_logo.svg" },
  { name: "Queen's University", logo: null },
  { name: "Western University", logo: null },
  { name: "SickKids", logo: null },
  { name: "Mount Sinai", logo: null },
  { name: "UHN", logo: null },
  { name: "Canadian Blood Services", logo: null },
  { name: "MaRS", logo: null },
  { name: "Resilience", logo: null },
  { name: "Therapure", logo: null },
  { name: "BlueRock Therapeutics", logo: null }
];

export const faqData = [
  {
    question: "What does 'turnkey' mean in your context?",
    answer: "Turnkey means we manage all major aspects of your cleanroom project—design, manufacture, construction, commissioning, validation, and long-term support—under one contract. This gives you a single point of accountability and simplifies project coordination."
  },
  {
    question: "What ISO classifications do you support?",
    answer: "We design and build cleanrooms across ISO 5-8 classifications. Our projects range from ISO 5 sterile filling suites to ISO 7/8 medical device assembly and controlled manufacturing environments."
  },
  {
    question: "What certifications do your products have?",
    answer: "Our products are ISO 9001:2015 certified, cGMP compliant, UL Listed, and ULC certified. We use premium UL listed materials designed for low bioburden and robust cleaning."
  },
  {
    question: "Do you provide training?",
    answer: "Yes, we offer comprehensive training programs including GMP pharma compliance, mechanical equipment operations, procedural requirements, general equipment maintenance, equipment handling, and cleaning and maintenance protocols."
  },
  {
    question: "What industries do you serve?",
    answer: "We serve (bio)pharmaceuticals, healthcare, medical device manufacturing, gene cell therapy, active pharmaceutical ingredients, biotech, and institutional settings including major universities and hospitals across North America."
  }
];

export const contactReasons = [
  "New Cleanroom Project",
  "Request a Consultation",
  "Laboratory Design",
  "Environmental Room Inquiry",
  "Retrofit/Upgrade",
  "Training Programs",
  "General Inquiry"
];

export const whyChooseUs = [
  {
    id: 1,
    title: "Local Canadian Presence",
    description: "Direct access to our experienced team with local expertise and support.",
    icon: "MapPin"
  },
  {
    id: 2,
    title: "Large Project Experience",
    description: "Proven track record delivering projects up to 60,000+ square feet.",
    icon: "Building2"
  },
  {
    id: 3,
    title: "High Quality Products",
    description: "UL Listed materials with premium finishes.",
    icon: "Award"
  },
  {
    id: 4,
    title: "Excellent Lead Times",
    description: "Significantly reduced architectural component lead times.",
    icon: "Clock"
  },
  {
    id: 5,
    title: "Technical Design Support",
    description: "Expert BIM and engineering guidance throughout your project.",
    icon: "Wrench"
  }
];

export const trainingPrograms = [
  "GMP Pharma Compliance",
  "Mechanical Equipment Operations",
  "Procedural Requirements",
  "General Equipment Maintenance",
  "Equipment Handling",
  "Cleaning and Maintenance",
  "Cleanroom Protocols",
  "Quality Management Systems"
];

export const designCapabilities = [
  "2D Layouts",
  "3D Architectural Panels",
  "3D Ducting & Utilities",
  "BMS/BAS P&ID",
  "Electrical Single Line",
  "3D Ductwork Routing",
  "BIM Coordination"
];
